import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
  Button,
  Chip,
  Stack,
  useTheme,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';
import { Calendar } from 'react-multi-date-picker';
import { DateObject } from 'react-multi-date-picker';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  getExternalQuotaForUser,
  getClientOfficeQuotaForUser,
  getWorkTravelQuotaForUser,
} from '../../admin/Services/EmployeePrivilegesService';
import { getHolidays } from '../../admin/Services/MasterdataService';
import { getUserData } from '../../services/UserService';
import KeycloakService from '../../services/KeycloakService';
import {
  ApplyExternalHrs,
  updateApplicationByUser,
} from '../../services/ExternalHrsService';
import { SHOW_NOTIFICATION } from '../../constants';
import { useDispatch } from 'react-redux';
import {
  toHolidaySet,
  countWorkingDays,
  extractHolidayRows,
} from './externalHoursWorkingDays';

const HOURS_PER_DAY_MIN = 1;
const HOURS_PER_DAY_MAX = 12;
const HOURS_PER_MONTH_MAX = 200;

const schema = z
  .object({
    rangeType: z.string().min(1, '*This field is required'),
    requestCategory: z.enum(['EXTERNAL', 'CLIENT_OFFICE', 'WORK_TRAVEL']),
    requestedHours: z.coerce.number({
      required_error: '*This field is required',
      invalid_type_error: '*This field is required',
    }),
    monthRange: z.array(z.any()).optional(),
    dateRange: z.array(z.any()).optional(),
    reason: z
      .string()
      .min(1, '*This field is required')
      .max(150, 'Max 150 characters allowed'),
  })
  .superRefine((data, ctx) => {
    const isDateRangeForm = data.requestCategory !== 'CLIENT_OFFICE';
    const max = isDateRangeForm ? HOURS_PER_DAY_MAX : HOURS_PER_MONTH_MAX;
    const min = HOURS_PER_DAY_MIN;
    if (
      typeof data.requestedHours !== 'number' ||
      Number.isNaN(data.requestedHours) ||
      data.requestedHours < min ||
      data.requestedHours > max
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isDateRangeForm
          ? `*provide value b/w ${HOURS_PER_DAY_MIN} and ${HOURS_PER_DAY_MAX}`
          : `*provide value b/w ${HOURS_PER_DAY_MIN} and ${HOURS_PER_MONTH_MAX}`,
        path: ['requestedHours'],
      });
    }
    if (isDateRangeForm) {
      if (!Array.isArray(data.dateRange) || data.dateRange.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '*Date range is required',
          path: ['dateRange'],
        });
      }
    } else if (!Array.isArray(data.monthRange) || data.monthRange.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '*Month range is required',
        path: ['monthRange'],
      });
    }
  });

const isWeekendDateObject = (dateObject) => {
  const weekDayIndex = dateObject?.weekDay?.index;
  return weekDayIndex === 0 || weekDayIndex === 6;
};

const monthArrayToDateObjectArray = (monthArray) => {
  if (!monthArray?.length) return [];
  const startMonth = monthArray[0];
  const startYear = monthArray[1];
  const endMonth = monthArray[2];
  const endYear = monthArray[3];
  const start = new DateObject(`${startYear}-${startMonth}-01`);
  const end = new DateObject(`${endYear}-${endMonth}-01`);
  return [start, end];
};

const datesToDateObjectArray = (startDate, endDate) => {
  if (!startDate) return [];
  const start = new DateObject(startDate);
  const end = new DateObject(endDate || startDate);
  if (start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')) {
    return [start];
  }
  return [start, end];
};

const ApplyExternalHours = ({
  toggleOpen,
  setRefresh,
  hours,
  reason,
  month = [],
  startDate: initialStartDate,
  endDate: initialEndDate,
  requestId,
  lockedCategory,
  initialRequestCategory,
}) => {
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const employeeId = KeycloakService.getUserDetail();
  const [openPicker, setOpenPicker] = useState(false);
  const [monthlyQuotaAllotted, setMonthlyQuotaAllotted] = useState(0);
  const [requiredMonth, setRequiredMonth] = useState(0);
  const [requiredYear, setRequiredYear] = useState(year);
  const [requestedHrs, setRequestedHrs] = useState(hours || '');
  const [holidaySet, setHolidaySet] = useState(new Set());
  const currentMonth = new Date().getMonth();

  const minDate = useMemo(() => {
    return new DateObject().subtract(1, 'month');
  }, [currentMonth]);

  const maxDate = useMemo(() => {
    if (currentMonth % 3 === 0) {
      return new DateObject().add(5, 'month');
    } else if (currentMonth % 3 === 1) {
      return new DateObject().add(4, 'month');
    } else return new DateObject().add(3, 'month');
  }, [currentMonth]);

  const dispatch = useDispatch();
  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      rangeType: 'CUSTOM',
      requestCategory:
        lockedCategory || initialRequestCategory || 'EXTERNAL',
      requestedHours: hours || '',
      reason: reason || '',
      monthRange: monthArrayToDateObjectArray(month),
      dateRange: datesToDateObjectArray(initialStartDate, initialEndDate),
    },
  });

  const rangeType = getValues('rangeType');
  const monthRange = watch('monthRange') || [];
  const dateRange = watch('dateRange') || [];
  const requestCategory = watch('requestCategory');
  const watchedHours = watch('requestedHours');
  const categoryLocked = Boolean(lockedCategory);
  const isDateRangeForm = requestCategory !== 'CLIENT_OFFICE';
  const categoryQuotaLabel =
    requestCategory === 'CLIENT_OFFICE'
      ? 'Client Office Hours'
      : requestCategory === 'WORK_TRAVEL'
        ? 'Work Travel Hours'
        : 'External Hours';

  const theme = useTheme();

  useEffect(() => {
    const loadHolidays = async () => {
      try {
        const user = await getUserData(employeeId);
        const locationId = user?.data?.locationId;
        if (!locationId) return;
        const holidayList = await getHolidays(locationId);
        setHolidaySet(toHolidaySet(extractHolidayRows(holidayList)));
      } catch (e) {
        // Holidays are best-effort for working-day math; weekends still apply.
      }
    };
    loadHolidays();
  }, [employeeId]);

  const resolvedDateBounds = useMemo(() => {
    if (!isDateRangeForm || !Array.isArray(dateRange) || dateRange.length === 0) {
      return { start: null, end: null };
    }
    const start = dateRange[0];
    const end = dateRange.length === 2 ? dateRange[1] : dateRange[0];
    return {
      start: start ? start.format('YYYY-MM-DD') : null,
      end: end ? end.format('YYYY-MM-DD') : null,
    };
  }, [dateRange, isDateRangeForm]);

  const workingDays = useMemo(
    () =>
      countWorkingDays(
        resolvedDateBounds.start,
        resolvedDateBounds.end,
        holidaySet,
      ),
    [resolvedDateBounds, holidaySet],
  );

  const hoursPerDay = Number(watchedHours) || 0;
  const cumulativeHours = workingDays * hoursPerDay;

  const handleMonthRangeChange = ({ value }) => {
    let updatedRange = [];

    if (rangeType === 'QUARTERLY' && value.length === 1) {
      if (value[0].month.index % 3 === 0)
        updatedRange = [value[0], new DateObject(value[0]).add(2, 'month')];
      else if (value[0].month.index % 3 === 1)
        updatedRange = [
          new DateObject(value[0]).subtract(1, 'month'),
          new DateObject(value[0]).add(1, 'month'),
        ];
      else
        updatedRange = [
          new DateObject(value[0]).subtract(2, 'month'),
          value[0],
        ];
      setOpenPicker(false);
    } else if (rangeType === 'MONTHLY') {
      updatedRange = [value[value.length - 1]];
      setOpenPicker(false);
    } else {
      updatedRange = value;
      if (value.length === 2) {
        setOpenPicker(false);
      }
    }

    setValue('monthRange', updatedRange);
    clearErrors('monthRange');
    if (updatedRange.length > 0) {
      setRequiredMonth(updatedRange[0].month.number);
      setRequiredYear(updatedRange[0].year);
    }
  };

  const handleDateRangeChange = ({ value }) => {
    const updatedRange = value || [];
    setValue('dateRange', updatedRange);
    clearErrors('dateRange');
    if (updatedRange.length === 2 || updatedRange.length === 1) {
      if (updatedRange.length === 2) {
        setOpenPicker(false);
      }
      setRequiredMonth(updatedRange[0].month.number);
      setRequiredYear(updatedRange[0].year);
    }
  };

  const getStringOfMonthRange = (range) => {
    if (!Array.isArray(range) || range.length === 0) return '';
    if (range.length === 2) {
      if (
        range[0]?.year === range[1]?.year &&
        range[0]?.monthIndex === range[1]?.monthIndex
      ) {
        return `${range[0]?.month?.name} ${range[0]?.year}`;
      }
      if (range[0]?.year === range[1]?.year) {
        return `${range[0]?.month?.name} - ${range[1]?.month?.name} ${range[0]?.year}`;
      }
      return `${range[0]?.month?.name} ${range[0]?.year} - ${range[1]?.month?.name} ${range[1]?.year}`;
    }
    return `${range[0]?.month?.name} ${range[0]?.year}`;
  };

  const getStringOfDateRange = (range) => {
    if (!Array.isArray(range) || range.length === 0) return '';
    const start = range[0]?.format?.('DD MMM YYYY') || '';
    if (range.length === 1) return start;
    const end = range[1]?.format?.('DD MMM YYYY') || start;
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const buildMonthRangePayload = (range) => {
    if (range.length === 1) {
      return [
        range[0].month.number,
        range[0].year,
        range[0].month.number,
        range[0].year,
      ];
    }
    if (range.length === 2) {
      return [
        range[0].month.number,
        range[0].year,
        range[1].month.number,
        range[1].year,
      ];
    }
    return [];
  };

  const onSubmit = async (data) => {
    const isExternal = data.requestCategory !== 'CLIENT_OFFICE';
    let monthRangePayload = [];
    let startDate = null;
    let endDate = null;

    if (isExternal) {
      const range = data.dateRange || [];
      if (!range.length) return;
      const startObj = range[0];
      const endObj = range.length === 2 ? range[1] : range[0];
      startDate = startObj.format('YYYY-MM-DD');
      endDate = endObj.format('YYYY-MM-DD');
      monthRangePayload = [
        startObj.month.number,
        startObj.year,
        endObj.month.number,
        endObj.year,
      ];
    } else {
      monthRangePayload = buildMonthRangePayload(data.monthRange || []);
    }

    const formattedPayload = {
      id: requestId ?? null,
      employeeId: KeycloakService.getUserDetail(),
      status: 'PENDING',
      rangeType: data.rangeType,
      requestCategory: data.requestCategory || 'EXTERNAL',
      requestedHours: data.requestedHours,
      reason: data.reason,
      tenantId: process.env.REACT_APP_TENANT_ID,
      monthRange: monthRangePayload,
      startDate,
      endDate,
      externalQuotaTime: monthlyQuotaAllotted,
    };

    let response;
    if (requestId) {
      response = await updateApplicationByUser(formattedPayload);
    } else {
      response = await ApplyExternalHrs(formattedPayload);
    }
    setRefresh((prev) => !prev);
    dipatchNotification(response.type, response.message);
    toggleOpen(false);
  };

  const getEmployeeExtQuota = async () => {
    let response;
    let quotaData;
    const month = requiredMonth - 1;
    if (requestCategory === 'CLIENT_OFFICE') {
      response = await getClientOfficeQuotaForUser(employeeId, requiredYear);
      quotaData = response?.data?.data?.clientOfficeHoursQuota?.thisYear || [];
    } else if (requestCategory === 'WORK_TRAVEL') {
      response = await getWorkTravelQuotaForUser(employeeId, requiredYear);
      quotaData = response?.data?.data?.workTravelHoursQuota?.thisYear || [];
    } else {
      response = await getExternalQuotaForUser(employeeId, requiredYear);
      quotaData = response?.data?.data?.externalHoursQuota?.thisYear || [];
    }
    if (rangeType === 'MONTHLY') {
      setMonthlyQuotaAllotted(parseInt(`${quotaData[month]}` || '0'));
    } else if (rangeType === 'QUARTERLY') {
      const nextMonthQuota = parseInt(`${quotaData[month + 1]}` || '0');
      const thirdMonthQuota = parseInt(`${quotaData[month + 2]}` || '0');

      setMonthlyQuotaAllotted(
        parseInt(`${quotaData[month]}` || '0') +
          nextMonthQuota +
          thirdMonthQuota,
      );
    }
  };

  useEffect(() => {
    if (requiredMonth) {
      getEmployeeExtQuota();
    }
  }, [requiredMonth, rangeType, requiredYear, requestCategory]);

  const rangeFieldError = isDateRangeForm ? errors.dateRange : errors.monthRange;
  const rangeValue = isDateRangeForm
    ? getStringOfDateRange(dateRange)
    : getStringOfMonthRange(monthRange);

  return (
    <Container>
      <Paper component={Box} elevation={0} sx={{ p: { xs: 2, sm: 5 } }}>
        <Grid container>
          <Grid
            container
            sx={{
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 2,
            }}
          >
            <Grid item>
              <IconButton onClick={toggleOpen}>
                <KeyboardBackspaceIcon />
              </IconButton>
            </Grid>
            <Grid xs={12}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  padding: '16px 12px 8px',
                  position: 'relative',
                  minHeight: '56px',
                }}
              >
                <InputLabel
                  sx={{
                    position: 'absolute',
                    top: '-8px',
                    left: '12px',
                    backgroundColor: 'white',
                    paddingX: '4px',
                    fontSize: '0.75rem',
                    color: theme.palette.text.secondary,
                  }}
                >
                  To
                </InputLabel>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={
                      requestCategory === 'CLIENT_OFFICE'
                        ? 'Senior Manager'
                        : 'Finance'
                    }
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
            <Tooltip
              title={`Quota for ${categoryQuotaLabel} is not additive — a new request updates the quota for that period instead of adding to it.`}
              arrow
            >
              <InfoOutlinedIcon
                color="action"
                sx={{ fontSize: '1.1rem', cursor: 'help' }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                {!categoryLocked && (
                  <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="request-category-label">
                      Hours Type
                    </InputLabel>
                    <Select
                      labelId="request-category-label"
                      label="Hours Type"
                      value={requestCategory || 'EXTERNAL'}
                      onChange={(e) => {
                        setValue('requestCategory', e.target.value);
                        clearErrors('requestCategory');
                        setValue('monthRange', []);
                        setValue('dateRange', []);
                      }}
                    >
                      <MenuItem value="EXTERNAL">External Hours</MenuItem>
                      <MenuItem value="CLIENT_OFFICE">
                        Client Office Hours
                      </MenuItem>
                      <MenuItem value="WORK_TRAVEL">
                        Work Travel Hours
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth>
                  <TextField
                    size="small"
                    fullWidth
                    value={rangeValue}
                    label={isDateRangeForm ? 'Date Range' : 'Month Range'}
                    placeholder={
                      isDateRangeForm
                        ? 'Select Date Range'
                        : 'Select Month Range'
                    }
                    onClick={() => setOpenPicker(true)}
                    error={!!rangeFieldError}
                    helperText={rangeFieldError?.message}
                    inputProps={{
                      style: {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Clear">
                            <IconButton
                              onClick={() => {
                                if (isDateRangeForm) {
                                  setValue('dateRange', []);
                                } else {
                                  setValue('monthRange', []);
                                }
                              }}
                            >
                              <RestartAltIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>

                <Dialog open={openPicker} onClose={() => setOpenPicker(false)}>
                  {isDateRangeForm ? (
                    <Calendar
                      range
                      rangeHover
                      value={dateRange}
                      minDate={minDate}
                      maxDate={maxDate}
                      onPropsChange={handleDateRangeChange}
                      mapDays={({ date }) => {
                        if (isWeekendDateObject(date)) {
                          return {
                            disabled: true,
                            style: { color: '#ccc' },
                          };
                        }
                        const key = date.format('YYYY-MM-DD');
                        if (holidaySet.has(key)) {
                          return {
                            disabled: true,
                            style: { color: '#ccc' },
                          };
                        }
                      }}
                    />
                  ) : (
                    <Calendar
                      onlyMonthPicker
                      range={rangeType !== 'MONTHLY'}
                      value={monthRange}
                      minDate={minDate}
                      maxDate={maxDate}
                      onPropsChange={handleMonthRangeChange}
                    />
                  )}
                </Dialog>
              </Grid>
              <Grid item xs={3.5}>
                <TextField
                  fullWidth
                  label={
                    isDateRangeForm
                      ? 'Request Hours/Day'
                      : 'Request Hours/Month'
                  }
                  type="text"
                  error={!!errors.requestedHours}
                  {...register('requestedHours', {
                    valueAsNumber: true,
                  })}
                  helperText={
                    errors.requestedHours?.message ||
                    (isDateRangeForm
                      ? `Min ${HOURS_PER_DAY_MIN}, max ${HOURS_PER_DAY_MAX}`
                      : '')
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    '& input[type=number]': {
                      '-moz-appearance': 'textfield',
                    },
                    '& .MuiOutlinedInput-root': {
                      display: 'flex',
                      alignItems: 'center',
                      '& fieldset': {},
                      '& input': {
                        textAlign: 'center',
                      },
                    },
                  }}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const max = isDateRangeForm
                      ? HOURS_PER_DAY_MAX
                      : HOURS_PER_MONTH_MAX;
                    const rangeMsg = `*provide value b/w ${HOURS_PER_DAY_MIN} and ${max}`;
                    if (inputValue === '' || inputValue === '0') {
                      setRequestedHrs('');
                      setError('requestedHours', { message: rangeMsg });
                    } else {
                      const cleanedValue = inputValue.replace(/^0+/, '');
                      const numericValue = Number(cleanedValue) || 0;

                      setRequestedHrs(numericValue);
                      setValue('requestedHours', numericValue);

                      if (
                        numericValue >= HOURS_PER_DAY_MIN &&
                        numericValue <= max
                      )
                        clearErrors('requestedHours');
                      else
                        setError('requestedHours', { message: rangeMsg });
                    }
                  }}
                />
              </Grid>
              {isDateRangeForm && (
                <>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Working Days"
                      value={workingDays}
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Cumulative Hours"
                      value={cumulativeHours}
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      Weekends and holidays are excluded. Same start and end
                      date counts as one day.
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <TextField
              size="small"
              fullWidth
              label="Reason"
              multiline
              rows={3}
              {...register('reason')}
              error={!!errors.reason}
              helperText={errors.reason?.message}
              value={watch('reason') ?? reason}
              onChange={(e) => setValue('reason', e.target.value)}
            />
          </Grid>

          <Grid container sx={{ mt: 3 }} justifyContent="space-between">
            <Button variant="outlined" color="error" onClick={toggleOpen}>
              Cancel
            </Button>
            <LoadingButton variant="contained" onClick={handleSubmit(onSubmit)}>
              Submit
            </LoadingButton>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ApplyExternalHours;
